import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, categoryMap } from '@/mocks/categories';
import { getArticlesByCategory, type Article } from '@/mocks/articles';

const catImages: Record<string, string> = {
  'zixing-yanbian': 'https://readdy.ai/api/search-image?query=Ancient%20Chinese%20character%20evolution%20chart%20from%20oracle%20bone%20to%20modern%2C%20warm%20sepia%20tones%2C%20traditional%20calligraphy%20on%20aged%20paper%2C%20scholarly%20museum%20display&width=800&height=500&seq=cat-zixing&orientation=landscape',
  'yuyi-jiexi': 'https://readdy.ai/api/search-image?query=Chinese%20character%20semantic%20analysis%20diagram%20with%20ancient%20dictionary%20pages%2C%20warm%20lighting%2C%20ink%20brush%20annotations%2C%20scholarly%20linguistics%20research&width=800&height=500&seq=cat-yuyi&orientation=landscape',
  'guji-yongli': 'https://readdy.ai/api/search-image?query=Ancient%20Chinese%20classical%20texts%20and%20bamboo%20slips%20displayed%20on%20wooden%20table%2C%20warm%20lighting%2C%20traditional%20library%20atmosphere%2C%20scholarly%20setting&width=800&height=500&seq=cat-guji&orientation=landscape',
  'wenhua-yanjiu': 'https://readdy.ai/api/search-image?query=Confucian%20classics%20and%20traditional%20Chinese%20cultural%20artifacts%2C%20warm%20sepia%20tones%2C%20museum%20quality%20display%2C%20scholarly%20atmosphere%2C%20ancient%20Chinese%20philosophy&width=800&height=500&seq=cat-wenhua&orientation=landscape',
  'xiandai-yujing': 'https://readdy.ai/api/search-image?query=Modern%20Chinese%20dictionary%20next%20to%20smartphone%20displaying%20Chinese%20text%2C%20blending%20traditional%20and%20digital%2C%20warm%20lighting%2C%20linguistics%20research&width=800&height=500&seq=cat-xiandai&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const category = slug ? categoryMap[slug] : undefined;
  const articles = slug ? getArticlesByCategory(slug) : [];
  const [activeCat, setActiveCat] = useState(slug || '');

  useEffect(() => {
    window.scrollTo(0, 0);
    if (slug) setActiveCat(slug);
  }, [slug]);

  const handleCategoryChange = (newSlug: string) => {
    setActiveCat(newSlug);
    navigate(`/category/${newSlug}`);
    window.scrollTo(0, 0);
  };

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="pt-20 pb-20">
          <div className="px-4 max-w-6xl mx-auto text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">分类未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您访问的分类不存在。</p>
            <Link to="/" className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: category.name },
  ];

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 淫字文化溯源`,
    description: category.description,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: articles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `https://yiyanse.github.io/article/${article.slug}`,
        name: article.title,
      })),
    },
  };

  return (
    <>
      <Navbar />
      <main>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }} />

        <section className="pt-20 pb-10 md:pb-14 bg-background-100">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            <div className="mb-4">
              <Breadcrumb items={breadcrumbItems} />
            </div>
            <div className="flex flex-col lg:flex-row items-start gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
                    <i className={`${category.icon} text-lg`}></i>
                  </span>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                    {category.name}
                  </h1>
                </div>
                <p className="text-sm text-foreground-500 leading-relaxed max-w-2xl">
                  {category.description}
                </p>
              </div>
              <div className="w-full lg:w-56">
                <img
                  src={catImages[category.slug] || catImages['zixing-yanbian']}
                  alt={category.name}
                  className="w-full h-36 object-cover object-top rounded-lg"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-3 bg-background-50 border-b border-background-200/70">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            <div className="flex items-center gap-2 overflow-x-auto pb-1 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeCat === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                  <span className="ml-1.5 text-xs opacity-70">({cat.articleCount})</span>
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            {articles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {articles.map((article) => (
                  <CategoryArticleCard key={article.slug} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}

const catArticleImages: Record<string, string> = {
  'jiaguwen-zhong-de-yin': 'https://readdy.ai/api/search-image?query=Oracle%20bone%20inscriptions%20closeup%20on%20turtle%20shell%2C%20warm%20museum%20lighting%2C%20ancient%20Chinese%20characters%2C%20scholarly%20archaeological%20photography&width=800&height=500&seq=cat-art-01&orientation=landscape',
  'yin-zi-wuti-yanbian': 'https://readdy.ai/api/search-image?query=Five%20Chinese%20calligraphy%20styles%20displayed%20side%20by%20side%2C%20ink%20on%20rice%20paper%2C%20warm%20lighting%2C%20elegant%20artistic%20presentation&width=800&height=500&seq=cat-art-02&orientation=landscape',
  'cong-yin-dao-yin': 'https://readdy.ai/api/search-image?query=Chinese%20character%20components%20radicals%20diagram%2C%20phonetic%20and%20semantic%20elements%2C%20warm%20paper%20texture%2C%20educational%20illustration&width=800&height=500&seq=cat-art-03&orientation=landscape',
  'yin-zi-ben-yi-kao': 'https://readdy.ai/api/search-image?query=Heavy%20rain%20over%20ancient%20Chinese%20village%2C%20ink%20wash%20painting%20style%2C%20atmospheric%2C%20traditional%20Chinese%20landscape%2C%20continuous%20rainfall%20depiction&width=800&height=500&seq=cat-art-04&orientation=landscape',
  'yin-zi-yuyi-wangluo': 'https://readdy.ai/api/search-image?query=Chinese%20character%20semantic%20network%20web%20diagram%20on%20aged%20paper%2C%20ink%20brush%20connections%2C%20scholarly%20linguistics%20visualization&width=800&height=500&seq=cat-art-05&orientation=landscape',
  'yin-yu-yin-yu-yao': 'https://readdy.ai/api/search-image?query=Three%20similar%20Chinese%20characters%20comparison%20chart%2C%20traditional%20dictionary%20layout%2C%20warm%20lighting%2C%20educational%20illustration&width=800&height=500&seq=cat-art-06&orientation=landscape',
  'shijing-zhong-de-yin': 'https://readdy.ai/api/search-image?query=Ancient%20Chinese%20Book%20of%20Songs%20poetry%20manuscript%20with%20annotations%2C%20warm%20lighting%2C%20traditional%20literature%2C%20scholarly%20atmosphere&width=800&height=500&seq=cat-art-07&orientation=landscape',
  'zuozhuan-xianqin-yin': 'https://readdy.ai/api/search-image?query=Ancient%20Chinese%20historical%20chronicles%20on%20bamboo%20slips%2C%20museum%20display%2C%20warm%20lighting%2C%20traditional%20Chinese%20historiography&width=800&height=500&seq=cat-art-08&orientation=landscape',
  'shuowenjiezi-xunshi-yin': 'https://readdy.ai/api/search-image?query=Shuowen%20Jiezi%20ancient%20Chinese%20dictionary%20pages%20with%20red%20annotations%2C%20warm%20lighting%2C%20traditional%20philology%2C%20scholarly%20study&width=800&height=500&seq=cat-art-09&orientation=landscape',
  'rujia-lijiao-zhong-de-yin': 'https://readdy.ai/api/search-image?query=Confucian%20classical%20texts%20on%20wooden%20desk%20with%20ink%20brush%2C%20warm%20lighting%2C%20traditional%20Chinese%20philosophy%2C%20scholarly%20atmosphere&width=800&height=500&seq=cat-art-10&orientation=landscape',
  'le-er-bu-yin-kongzi': 'https://readdy.ai/api/search-image?query=Confucius%20teaching%20scene%20in%20traditional%20Chinese%20ink%20wash%20painting%2C%20warm%20sepia%2C%20classical%20aesthetic%2C%20harmonious%20composition&width=800&height=500&seq=cat-art-11&orientation=landscape',
  'yin-zi-xiandai-hanyu': 'https://readdy.ai/api/search-image?query=Modern%20Chinese%20language%20research%20with%20dictionary%20and%20computer%2C%20corpus%20linguistics%2C%20warm%20lighting%2C%20academic%20setting&width=800&height=500&seq=cat-art-12&orientation=landscape',
};

function CategoryArticleCard({ article }: { article: Article }) {
  const cat = categoryMap[article.category];
  const imgSrc = catArticleImages[article.slug] || catArticleImages['jiaguwen-zhong-de-yin'];
  return (
    <article className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden transition-all duration-300 hover:border-primary-200">
      <Link to={`/article/${article.slug}`} className="block cursor-pointer">
        <div className="relative h-44 md:h-48 overflow-hidden">
          <img
            src={imgSrc}
            alt={article.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          {cat && (
            <span className="absolute top-3 left-3 px-2.5 py-1 bg-background-50/90 backdrop-blur-sm rounded-md text-xs font-medium text-foreground-700">
              {cat.name}
            </span>
          )}
        </div>
        <div className="p-4 md:p-5">
          <h2 className="font-heading text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-700 transition-colors duration-200">
            {article.title}
          </h2>
          <p className="text-xs text-foreground-500 leading-relaxed mb-4 line-clamp-2">
            {article.excerpt}
          </p>
          <div className="flex items-center justify-between text-xs text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.readTime}
            </span>
            <span>{article.publishDate}</span>
          </div>
        </div>
      </Link>
    </article>
  );
}